import React, { useCallback, useMemo } from 'react';
import { Flex, Heading, Icon, Text, Button } from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { UseMutateFunction } from 'react-query';
import {
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdLocationOn,
  MdPeople,
  MdPerson3,
} from 'react-icons/md';
import { AiOutlineHome } from 'react-icons/ai';
import { useUser } from '../hooks/useUser';
import { useAuthContext } from '../AuthProvider';
import { Schedule } from '../models/schedule';
import BoardGameItem from './BoardGameItem';
import { toDateFormat } from '../utils/timestamp';
import Header from './Header';

interface ScheduleInfoProps {
  groupId: string;
  schedule: Schedule;
  participate: (userId: string) => void;
  cancel: (userId: string) => void;
  remove: UseMutateFunction<void, unknown, void, unknown>;
}

const ScheduleInfo = ({
  groupId,
  schedule,
  participate,
  cancel,
  remove,
}: ScheduleInfoProps) => {
  const owner = useUser(schedule.owner);
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const isOwner = user?.uid === owner?.uid;
  const isMember = schedule.members.includes(user?.uid ?? '');
  const now = Date.now();
  const isUpcoming = now < schedule.start.toDate().getTime();
  const isFull = schedule.members.length === schedule.total;

  const statusTxt = useMemo(() => {
    if (!isUpcoming) {
      return '종료';
    }
    if (isFull) {
      return '모집 완료';
    }
    return '모집중';
  }, [isUpcoming, isFull]);

  const statusColor = useMemo(() => {
    if (isUpcoming && !isFull) {
      return '#D84E46';
    }
    return '#C6C6C6';
  }, [isUpcoming, isFull]);

  const buttonTxt = useMemo(() => {
    if (!isUpcoming) {
      return '모집 종료';
    }
    if (isMember) {
      return '참여 취소하기';
    }
    if (isFull) {
      return '모집 종료';
    }
    return '참여하기';
  }, [isUpcoming, isFull, isMember]);

  const buttonBgColor = useMemo(() => {
    if (!isUpcoming) {
      return '#F0F0F0';
    }
    if (isMember) {
      return 'white';
    }
    if (isFull) {
      return '#F0F0F0';
    }
    return '#D84E46';
  }, [isUpcoming, isFull, isMember]);

  const buttonColor = useMemo(() => {
    if (!isUpcoming) {
      return '#9E9E9E';
    }
    if (isMember) {
      return '#D84E46';
    }
    if (isFull) {
      return '#9E9E9E';
    }
    return 'white';
  }, [isUpcoming, isFull, isMember]);

  const handleClick = useCallback(() => {
    if (!isUpcoming) {
      return;
    }
    if (isMember) {
      cancel(user?.uid!);
      return;
    }
    if (isFull) {
      return;
    }
    participate(user?.uid!);
  }, [isUpcoming, isMember, isFull, participate, cancel, user]);

  const handleRemove = useCallback(async () => {
    await remove();
    navigate(-1);
  }, [remove, navigate]);

  return (
    <Flex flexDir="column" width="100%">
      <Flex
        pos="relative"
        h="100%"
        flexDirection="column"
        align="center"
        px="1rem"
      >
        <Header
          title=""
          leftIcon={MdChevronLeft}
          leftTo={-1}
          rightIcon={AiOutlineHome}
          rightTo="/"
        />

        <Flex
          justifyContent="space-between"
          alignItems="top"
          width="100%"
          pb="12px"
        >
          <Heading as="h2" size="lg" lineHeight="tall" textAlign="center">
            {schedule.name}
          </Heading>
          <Text
            px="8px"
            py="4px"
            borderRadius="4px"
            bgColor={statusColor}
            color="white"
            fontSize="20px"
            lineHeight="20px"
            fontWeight="bold"
            height="32px"
          >
            {statusTxt}
          </Text>
        </Flex>
        <Flex
          flexDir="column"
          alignItems="start"
          width="100%"
          pb="16px"
          borderBottom="1px solid #E8E8E8"
        >
          <Flex flexDir="column" alignItems="start" width="100%" mt="24px">
            <Flex justify="space-between" width="100%" alignItems="start">
              <Flex flexDir="column" justify="start" flex="1" color="#383838">
                <Flex alignItems="center" mb="8px">
                  <Icon as={MdCalendarToday} mr="6px" />
                  <Text fontWeight="semibold">
                    {toDateFormat(schedule.start, true)}
                  </Text>
                </Flex>
                <Flex alignItems="center" mb="8px">
                  <Icon as={MdLocationOn} mr="6px" />
                  <Text fontWeight="semibold">{schedule.location}</Text>
                </Flex>
                <Flex alignItems="center">
                  <Icon as={MdPeople} mr="6px" />
                  <Text fontWeight="semibold">
                    {schedule.members.length}/{schedule.total}
                  </Text>
                </Flex>
              </Flex>
              <Flex alignItems="center">
                <Icon
                  borderRadius="50%"
                  as={MdPerson3}
                  background="#D84E46"
                  color="white"
                />
                <Text ml="0.5rem" as="b">
                  {owner?.name}
                </Text>
              </Flex>
            </Flex>
            <Text mt="12px" whiteSpace="pre-line" width="100%">
              {schedule.description}
            </Text>
          </Flex>
        </Flex>
        <Link
          style={{ width: '100%' }}
          to={`/group/${groupId}/schedule/${schedule.id}/members`}
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            py="24px"
            borderBottom="1px solid #E8E8E8"
          >
            <Text as="b" fontSize="18px">
              모임 멤버
            </Text>
            <Text fontSize="14px" color="#858585" ml="14px" mr="auto">
              {schedule.members.length || 0}명
            </Text>
            <Icon as={MdChevronRight} boxSize="24px" color="black" />
          </Flex>
        </Link>
        <Flex flexDir="column" width="100%" alignItems="start">
          <Text
            pt="16px"
            pb="14px"
            fontSize="18px"
            color="#383838"
            fontWeight="bold"
          >
            플레이할 보드게임
          </Text>
          <Flex width="100%" flexDir="column" gap="13px">
            {schedule.boardGames.map((boardGameId) => (
              <BoardGameItem key={boardGameId} boardGameId={boardGameId} />
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flexDir="column"
        gap="16px"
        borderTop="1px solid #D9D9D9"
        mt="40px"
        pt="12px"
        pb="34px"
        px="1rem"
      >
        <Button
          onClick={handleClick}
          width="100%"
          py="14px"
          borderRadius="40px"
          color={buttonColor}
          bgColor={buttonBgColor}
          borderColor="#D84E46"
          borderWidth={isUpcoming && isMember ? '1px' : 'none'}
        >
          {buttonTxt}
        </Button>
        {isOwner && (
          <Button
            onClick={handleRemove}
            width="100%"
            py="14px"
            borderRadius="40px"
            color="white"
            bgColor="#D84E46"
            borderColor="#D84E46"
            borderWidth="none"
          >
            삭제하기
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default ScheduleInfo;
